/**
 * offlineStore.js
 * ----------------
 * Lean Offline Mode storage layer — IndexedDB-backed cache of history and
 * queued offline drafts. Replaces the previous lossy localStorage cache.
 *
 * Two object stores:
 *   - `history_cache`: keyed by entry `id`. Stores last 50 deduped history
 *     entries so the provider can READ + COPY their notes offline.
 *   - `offline_drafts`: keyed by client-generated draft id. Stores FULL state
 *     snapshots (notes + ROS/PE toggles + ICD-10s + deferred topics + visit
 *     type + demographics) when the provider hits "Save & Next Patient" while
 *     offline. On reconnect, the drafts can be drained back to the server.
 *
 * Quota / size:
 *   - History cap: 50 entries (FIFO by timestamp desc)
 *   - Drafts: unlimited but pruned to last 200 to avoid quota blowups
 *
 * All operations are async + return promises. Failures are swallowed and
 * logged — never throw, because the offline store is BEST-EFFORT (we never
 * want a broken IDB to block the foreground generate flow).
 */

const DB_NAME = 'writeright_offline_v1';
const DB_VERSION = 1;
const STORES = {
  HISTORY: 'history_cache',
  DRAFTS: 'offline_drafts',
};
const HISTORY_CAP = 50;
const DRAFTS_CAP = 200;

let dbPromise = null;

const openDB = () => {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB unavailable'));
  }
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORES.HISTORY)) {
        const store = db.createObjectStore(STORES.HISTORY, { keyPath: 'id' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.DRAFTS)) {
        const store = db.createObjectStore(STORES.DRAFTS, { keyPath: 'id' });
        store.createIndex('saved_at', 'saved_at', { unique: false });
      }
    };
  });
  return dbPromise;
};

const tx = (storeName, mode = 'readonly') =>
  openDB().then((db) => db.transaction(storeName, mode).objectStore(storeName));

const promisify = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

// ── History cache API ──

/** Replace the entire cached history list with the latest N items. */
export const cacheHistory = async (entries) => {
  if (!Array.isArray(entries)) return;
  try {
    const store = await tx(STORES.HISTORY, 'readwrite');
    await promisify(store.clear());
    // Sort desc by timestamp, keep top HISTORY_CAP
    const sorted = entries
      .filter((e) => e && e.id)
      .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''))
      .slice(0, HISTORY_CAP);
    for (const entry of sorted) {
      try {
        // Strip team_notes (can be huge) and any transient fields
        const minimal = {
          id: entry.id,
          clinical_note: entry.clinical_note,
          admin_letter: entry.admin_letter,
          indication: entry.indication,
          referrals: entry.referrals,
          imaging_orders: entry.imaging_orders,
          icd10_suggestions: entry.icd10_suggestions,
          chief_complaint: entry.chief_complaint,
          patient_age: entry.patient_age,
          patient_gender: entry.patient_gender,
          patient_status: entry.patient_status,
          patient_name: entry.patient_name,
          patient_label: entry.patient_label,
          encounter_type: entry.encounter_type,
          visit_type: entry.visit_type,
          note_title: entry.note_title,
          original_notes: entry.original_notes,
          pe_exceptions: entry.pe_exceptions,
          deferred_topics: entry.deferred_topics,
          provider_name: entry.provider_name,
          timestamp: entry.timestamp || entry.created_at,
        };
        await promisify(store.put(minimal));
      } catch (e) {
        console.warn('[offlineStore] cacheHistory put failed for', entry.id, e);
      }
    }
  } catch (err) {
    console.warn('[offlineStore] cacheHistory failed:', err?.message);
  }
};

/** Return all cached history entries, newest first. */
export const getCachedHistory = async () => {
  try {
    const store = await tx(STORES.HISTORY, 'readonly');
    const all = await promisify(store.getAll());
    return (all || []).sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
  } catch (err) {
    console.warn('[offlineStore] getCachedHistory failed:', err?.message);
    return [];
  }
};

// ── Offline drafts queue API ──

/** Push a full state snapshot into the offline queue. Returns the draft id.
 *  Feb 28, 2026 — Also stamps an `idempotency_key` so that when the queue
 *  drains and posts to /api/history/save-draft, the backend can detect a
 *  replay (network flap, service-worker retry, double-tap) and UPSERT
 *  instead of creating duplicate history entries. The key is the draft id
 *  itself since it's already a stable client-generated UUID. */
export const queueOfflineDraft = async (snapshot) => {
  const id = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const draft = {
    id,
    saved_at: new Date().toISOString(),
    status: 'pending', // pending | syncing | failed
    idempotency_key: id, // see jsdoc above
    ...snapshot,
  };
  try {
    const store = await tx(STORES.DRAFTS, 'readwrite');
    await promisify(store.put(draft));
    // Prune if over cap
    const count = await promisify(store.count());
    if (count > DRAFTS_CAP) {
      const all = await promisify(store.getAll());
      const sorted = all.sort((a, b) => (a.saved_at || '').localeCompare(b.saved_at || ''));
      const toDelete = sorted.slice(0, count - DRAFTS_CAP);
      for (const d of toDelete) await promisify(store.delete(d.id));
    }
    window.dispatchEvent(new CustomEvent('writeright:offline-queue-changed'));
    return id;
  } catch (err) {
    console.warn('[offlineStore] queueOfflineDraft failed:', err?.message);
    return null;
  }
};

/** Get all queued drafts, newest first. */
export const getQueuedDrafts = async () => {
  try {
    const store = await tx(STORES.DRAFTS, 'readonly');
    const all = await promisify(store.getAll());
    return (all || []).sort((a, b) => (b.saved_at || '').localeCompare(a.saved_at || ''));
  } catch (err) {
    console.warn('[offlineStore] getQueuedDrafts failed:', err?.message);
    return [];
  }
};

/** Remove a queued draft (e.g. after successful drain to server). */
export const removeQueuedDraft = async (id) => {
  try {
    const store = await tx(STORES.DRAFTS, 'readwrite');
    await promisify(store.delete(id));
    window.dispatchEvent(new CustomEvent('writeright:offline-queue-changed'));
  } catch (err) {
    console.warn('[offlineStore] removeQueuedDraft failed:', err?.message);
  }
};

/** Update status/metadata for a queued draft (e.g. mark as syncing/failed). */
export const updateQueuedDraft = async (id, patch) => {
  try {
    const store = await tx(STORES.DRAFTS, 'readwrite');
    const existing = await promisify(store.get(id));
    if (!existing) return;
    await promisify(store.put({ ...existing, ...patch }));
    window.dispatchEvent(new CustomEvent('writeright:offline-queue-changed'));
  } catch (err) {
    console.warn('[offlineStore] updateQueuedDraft failed:', err?.message);
  }
};

/** Count of currently queued drafts (cheap helper for badge). */
export const getQueueCount = async () => {
  try {
    const store = await tx(STORES.DRAFTS, 'readonly');
    return await promisify(store.count());
  } catch {
    return 0;
  }
};

export const OFFLINE_QUEUE_EVENT = 'writeright:offline-queue-changed';
